"use client";

import { Time } from "@commerce/components/time";
import { orpc } from "@shared/lib/orpc";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@ui/components/collapsible";
import { AnchorLinkButton } from "@ui/components/link-button";
import { Skeleton } from "@ui/components/skeleton";
import { Spinner } from "@ui/components/spinner";
import { toast } from "@ui/components/toast";
import { ChevronDownIcon, PhoneIcon, PlayIcon } from "lucide-react";
import { type ReactNode, useRef, useState } from "react";

/** Where a turn sits in its call, when the call service recorded it (`callOffsetMs`). */
export interface CallTurn {
	id: string;
	callOffsetMs: number | null;
}

const STATUS: Record<
	string,
	{
		label: string;
		variant: "success" | "warning" | "destructive" | "secondary" | "info";
	}
> = {
	RINGING: { label: "Ringing", variant: "info" },
	CONNECTING: { label: "Connecting", variant: "info" },
	LIVE: { label: "Live", variant: "info" },
	ENDED: { label: "Completed", variant: "success" },
	MISSED: { label: "Missed", variant: "warning" },
	REJECTED: { label: "Rejected", variant: "secondary" },
	FAILED: { label: "Failed", variant: "destructive" },
};

/** "3:42": a call's length or a turn's offset in it. */
export function clockOf(ms: number): string {
	const total = Math.max(0, Math.round(ms / 1000));
	const minutes = Math.floor(total / 60);
	const seconds = total % 60;
	return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/**
 * A WhatsApp call inside a transcript: its outcome and length, the recording (fetched as a
 * 5-minute signed link only when played), its summary, and the turns said on it. A turn's time
 * seeks the recording to the moment it was said.
 */
export function CallCard({
	organizationId,
	callId,
	turns,
	children,
}: {
	organizationId: string;
	callId: string;
	turns: readonly CallTurn[];
	/** The call's turns, drawn like the rest of the transcript. */
	children: (seek: (offsetMs: number) => void) => ReactNode;
}) {
	const queryClient = useQueryClient();
	const { organizationSlug } = useParams({ strict: false });
	const audio = useRef<HTMLAudioElement>(null);
	const [loading, setLoading] = useState(false);
	const [src, setSrc] = useState<string | null>(null);
	const call = useQuery(
		orpc.calls.get.queryOptions({ input: { organizationId, callId } }),
	);

	const play = async (offsetMs?: number) => {
		const element = audio.current;
		if (!element) {
			return;
		}
		if (!src) {
			setLoading(true);
			try {
				const { url } = await queryClient.fetchQuery({
					...orpc.calls.recordingUrl.queryOptions({
						input: { organizationId, callId },
					}),
					// Signed for 5 minutes: fetched again after that.
					staleTime: 4 * 60_000,
				});
				setSrc(url);
				element.src = url;
			} catch {
				toast.add({
					type: "error",
					title: "Recording unavailable",
					description:
						"It may not have been recorded, or it was deleted under the retention rules.",
				});
				return;
			} finally {
				setLoading(false);
			}
		}
		if (offsetMs !== undefined) {
			element.currentTime = offsetMs / 1000;
		}
		await element.play().catch(() => undefined);
	};

	const data = call.data;

	return (
		<div className="flex flex-col gap-3 rounded-3xl bg-card p-4 ring-1 ring-foreground/6 dark:ring-foreground/10">
			<div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
				<PhoneIcon
					className="size-4 text-muted-foreground"
					aria-hidden
				/>
				<span className="font-medium">Call</span>
				{call.isPending ? (
					<Skeleton className="h-4 w-28" />
				) : data ? (
					<CallFacts call={data} />
				) : null}
				{organizationSlug ? (
					<AnchorLinkButton
						variant="ghost"
						size="sm"
						className="ml-auto"
						href={`/app/${organizationSlug}/calls?id=${encodeURIComponent(callId)}`}
					>
						Open the call
					</AnchorLinkButton>
				) : null}
			</div>

			{data?.hasRecording ? (
				<div className="flex items-center gap-2">
					{src ? null : (
						<Button
							variant="outline"
							size="sm"
							onClick={() => play()}
							disabled={loading}
						>
							{loading ? (
								<Spinner data-icon="inline-start" />
							) : (
								<PlayIcon data-icon="inline-start" />
							)}
							Play the recording
						</Button>
					)}
					<audio
						ref={audio}
						controls
						preload="none"
						className={src ? "h-9 w-full" : "hidden"}
						aria-label="Call recording"
					>
						<track kind="captions" label="Transcript below" />
					</audio>
				</div>
			) : null}

			{data?.summary ? (
				<p className="text-pretty text-sm" dir="auto">
					<span className="text-muted-foreground">Summary: </span>
					{data.summary}
				</p>
			) : null}

			<Collapsible>
				<CollapsibleTrigger
					render={
						<Button
							variant="ghost"
							size="sm"
							className="-ml-2 w-fit text-muted-foreground data-panel-open:[&>svg]:rotate-180"
						/>
					}
				>
					Show {turns.length} {turns.length === 1 ? "turn" : "turns"}
					<ChevronDownIcon data-icon="inline-end" />
				</CollapsibleTrigger>
				<CollapsibleContent className="flex flex-col gap-6 pt-3">
					{children((offsetMs) => void play(offsetMs))}
				</CollapsibleContent>
			</Collapsible>
		</div>
	);
}

/** How long the call lasted, how it ended and when it rang. */
function CallFacts({
	call,
}: {
	call: {
		durationSeconds: number | null;
		status: string;
		ringingAt: Date | string;
	};
}) {
	const status = STATUS[call.status] ?? null;
	return (
		<>
			{call.durationSeconds != null ? (
				<span className="tabular">
					{clockOf(call.durationSeconds * 1000)}
				</span>
			) : null}
			{status ? (
				<Badge variant={status.variant}>{status.label}</Badge>
			) : null}
			<span className="text-muted-foreground text-xs">
				<Time at={new Date(call.ringingAt)} label="Rang" />
			</span>
		</>
	);
}

/** A turn's place in the recording; a button that plays from there when there is one. */
export function CallOffset({
	offsetMs,
	onSeek,
}: {
	offsetMs: number | null;
	onSeek: (offsetMs: number) => void;
}) {
	if (offsetMs == null) {
		return null;
	}
	return (
		<button
			type="button"
			onClick={() => onSeek(offsetMs)}
			className="rounded-md font-mono text-muted-foreground text-xs tabular outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/30"
			aria-label={`Play from ${clockOf(offsetMs)}`}
		>
			{clockOf(offsetMs)}
		</button>
	);
}
